import {
  buildSeamSegments,
  reverseSeamHandles,
  seamPlacementAt,
  type SeamHandle,
} from "./seamGeometry";
import {
  advancedStitchLayoutEnabled,
  effectiveCornerAngle,
  samplePolylineHolesEvenCorners,
  seamCornerIndices,
  usesCornerLayout,
} from "./stitchLayout";
import {
  pieceStitchLines,
  resolvePartStitchingDefaults,
  type Piece,
  type Project,
  type StitchingDefaults,
  type StitchLine,
  type StitchPitchMode,
  type StitchShape,
  type Vec2,
} from "./Project";

export interface StitchHolePlacement {
  /** Center of the hole in project coordinates. */
  position: Vec2;
  /** Local stitch direction at this hole, in degrees (0 = +x, 90 = +y).
   *  Used to orient slant/diamond chisel marks along the line. */
  tangentDeg: number;
  /** #687: false when this hole does NOT sit on its matched main's pitch — a
   *  matched follower whose count overflows its (shorter) seam at the main's
   *  pitch, or a "fill working area" follower whose even spread differs from the
   *  main's pitch. Absent / true for a normal, on-pitch hole. The pattern rings
   *  these so they're obvious when punching. */
  conforming?: boolean;
  /** #687: true when this is a main hole to SKIP when sewing (every Nth, where
   *  N = the follower's `skipEvery`). It's still punched; it just has no partner
   *  on the fewer-holed follower, so the pattern slashes it. */
  skip?: boolean;
  /** #691: this hole is the one at a corner of the seam, so it is punched ROUND
   *  whatever chisel the rest of the row uses. Set only when the seam asks for
   *  it — see `roundCornerHoles`. */
  corner?: boolean;
}

/**
 * Walk the polyline at exact `pitch` increments and emit a hole at each
 * step. The first hole sits on the first vertex; subsequent holes are at
 * `i * pitch` along the line, stopping at the last position that still fits
 * within the polyline's total length. Pitch is honoured exactly — if the
 * line's length isn't a clean multiple, the trailing remainder is left
 * empty rather than re-distributed across the row.
 *
 * The placement uses arc-length parameterisation so corners on a polyline
 * keep the spacing correct across vertices.
 */
/** Total seam length in mm (#199 — used to seed / spread a matched count). */
export function stitchLineLength(line: StitchLine): number {
  return buildSeamSegments(line.points, line.closed === true, line.handles)
    .total;
}

/** True when the seam forms a closed loop — explicitly flagged `closed`, OR an
 *  open polyline whose first and last vertices coincide (within a hole
 *  diameter). A loop is stitched as a continuous RING: its `count` holes spread
 *  evenly all the way around (no end-pinning), so a 3-hole circle gets 3 holes
 *  120° apart — not two stacked at the seam plus one opposite. */
export function isStitchLoop(line: StitchLine): boolean {
  if (line.closed === true) return true;
  const pts = line.points;
  if (pts.length < 3) return false;
  const a = pts[0]!;
  const b = pts[pts.length - 1]!;
  return Math.hypot(a.x - b.x, a.y - b.y) < Math.max(line.size, 1e-6);
}

export function samplePolylineHoles(
  points: Vec2[],
  pitch: number,
  startOffset: number = 0,
  closed: boolean = false,
  /** Absolute arc-length window `[start, end]` the holes are confined to (the
   *  seam's "working area"). When given, holes march from `start` by `pitch`
   *  up to `end` — both ends inset, `startOffset` ignored. Used for open seams
   *  (and inset closed loops, which become an arc). */
  window: { start: number; end: number } | null = null,
  /** Bezier handles for `points` (#982). Absent = every run straight, which is
   *  what every seam saved before curves existed carries. */
  handles?: SeamHandle[],
): StitchHolePlacement[] {
  if (points.length < 2 || pitch <= 0) return [];

  const { segs, total } = buildSeamSegments(points, closed, handles);
  if (segs.length === 0) return [];

  if (window) {
    // A closed loop's window may run past the seam (it's measured from the
    // start-of-path origin), so positions wrap around the ring; an open seam's
    // window is clamped to its ends.
    const lo = closed
      ? window.start
      : Math.max(0, Math.min(total, window.start));
    const hi = closed ? window.end : Math.max(0, Math.min(total, window.end));
    if (hi < lo - 1e-9) return [];
    const at = (d: number) =>
      closed
        ? seamPlacementAt(segs, modPos(d, total))
        : seamPlacementAt(segs, Math.min(total, d));
    const out: StitchHolePlacement[] = [];
    for (let i = 0; ; i++) {
      const d = lo + i * pitch;
      if (d > hi + 1e-6) break;
      out.push(at(d));
    }
    return out;
  }

  // No window → a continuous closed ring (or a full open line). The first hole
  // sits at `startOffset`; a closed ring rotates the whole row around from there
  // (full-range origin), while an open line keeps its historical wrap into one
  // pitch interval so a too-large value never drops the first hole.
  const upper = closed ? total - 1e-6 : total + 1e-6;
  const out: StitchHolePlacement[] = [];
  if (closed) {
    for (let i = 0; i * pitch <= upper; i++) {
      out.push(seamPlacementAt(segs, modPos(startOffset + i * pitch, total)));
    }
  } else {
    const off = ((startOffset % pitch) + pitch) % pitch;
    for (let i = 0; off + i * pitch <= upper; i++) {
      out.push(seamPlacementAt(segs, Math.min(total, off + i * pitch)));
    }
  }
  return out;
}

/** Positive modulo — wraps an arc-length distance into `[0, total)`. */
function modPos(d: number, total: number): number {
  return total > 0 ? ((d % total) + total) % total : 0;
}

/** First grid hole at or after `lo`, for a construction-anchored row (#199
 *  Phase 3). Holes sit at `gridPhase + k·pitch`; this returns the smallest such
 *  value `>= lo`. Because the grid is shared by every seam on the same source
 *  construction line, two seams at the same pitch place holes at the same points
 *  and line up where they overlap. */
export function gridWindowStart(
  gridPhase: number,
  lo: number,
  pitch: number,
): number {
  if (pitch <= 0) return lo;
  const k = Math.ceil((lo - gridPhase) / pitch - 1e-9);
  return gridPhase + k * pitch;
}

/**
 * Place exactly `count` holes spread EVENLY along the polyline, regardless of
 * length (#199 stitch matching). An OPEN seam pins a hole on both endpoints
 * (`count - 1` equal gaps); a CLOSED loop spaces `count` holes around it with no
 * duplicate at the seam. Returns `[]` for `count < 1` or a degenerate line, and
 * a single start hole for `count === 1`.
 */
export function samplePolylineHolesByCount(
  points: Vec2[],
  count: number,
  closed: boolean = false,
  startOffset: number = 0,
  /** Absolute arc-length window `[start, end]` to spread the count across (the
   *  seam's "working area"). When given, the `count` holes are pinned to both
   *  ends of the window and `startOffset` is ignored. */
  window: { start: number; end: number } | null = null,
  /** Bezier handles for `points` (#982). Absent = every run straight, which is
   *  what every seam saved before curves existed carries. */
  handles?: SeamHandle[],
): StitchHolePlacement[] {
  if (points.length < 2 || count < 1) return [];
  const { segs, total } = buildSeamSegments(points, closed, handles);
  if (segs.length === 0 || total <= 0) return [];
  const n = Math.floor(count);

  if (window) {
    // Closed windows are measured from the start-of-path origin and wrap; open
    // windows clamp to the seam ends.
    const lo = closed
      ? window.start
      : Math.max(0, Math.min(total, window.start));
    const hi = closed ? window.end : Math.max(lo, Math.min(total, window.end));
    const at = (d: number) =>
      closed
        ? seamPlacementAt(segs, modPos(d, total))
        : seamPlacementAt(segs, Math.min(total, d));
    if (n === 1) return [at(lo)];
    const step = (hi - lo) / (n - 1);
    const out: StitchHolePlacement[] = [];
    for (let i = 0; i < n; i++) {
      out.push(at(lo + i * step));
    }
    return out;
  }

  if (n === 1) return [seamPlacementAt(segs, modPos(startOffset, total))];
  const out: StitchHolePlacement[] = [];
  if (closed) {
    // Evenly-spaced ring rotated so the first hole sits at the start origin.
    for (let i = 0; i < n; i++) {
      out.push(
        seamPlacementAt(segs, modPos(startOffset + (i * total) / n, total)),
      );
    }
  } else {
    const base = total / Math.max(1, n - 1);
    const off = base > 0 ? ((startOffset % base) + base) % base : 0;
    // Open seam: keep the last hole pinned to the end and nudge the start by
    // the offset, re-spacing the N holes across the remaining length (so a
    // zero offset gives the classic both-ends-pinned row).
    const step = (total - off) / (n - 1);
    for (let i = 0; i < n; i++) {
      out.push(seamPlacementAt(segs, Math.min(total, off + i * step)));
    }
  }
  return out;
}

/**
 * Place exactly `count` holes at a FIXED `spacing` (mm), marching from the
 * window's start — the matched follower's "Match pitch exactly" mode (#199), so
 * it lays holes at the SAME pitch as its main. Unlike `samplePolylineHolesByCount`
 * the holes are NOT pinned to the window end: if `count` holes at `spacing` are
 * longer than the working area they run on toward the seam's end (clamped to the
 * total length); if shorter, the tail is left empty. Open seams only.
 *
 * When `windowEnd` (the working area's END handle, an absolute arc-length) is
 * given, the row is TRUNCATED there (#742): a hole that lands past the handle but
 * still on the seam is not placed, so dragging the end-of-work-area handle inward
 * drops trailing holes while the surviving row keeps the main's pitch from the
 * start. The START handle is symmetric (#905): raising `start` marches the row
 * inward at the same pitch, and a hole the follower's length COULD hold (its
 * un-inset position is on the seam) but the start has pushed past the seam end is
 * likewise dropped — not pinned to the end with a squished final gap. Holes past
 * the seam TOTAL *from the origin* are a different case — a genuine count overflow
 * — and are still clamped to the end and flagged non-conforming (#687), regardless
 * of the handle. Left `windowEnd` undefined and the row keeps the old
 * run-to-seam-end behavior with no work-area truncation.
 */
export function samplePolylineHolesAtSpacing(
  points: Vec2[],
  count: number,
  spacing: number,
  /** Absolute arc-length the first hole starts at (the working-area start). */
  start: number = 0,
  /** Absolute arc-length of the working area's END handle. When set, holes past
   *  it are truncated (not placed) so the end handle limits the row (#742). */
  windowEnd?: number,
  /** Bezier handles for `points` (#982). Absent = every run straight, which is
   *  what every seam saved before curves existed carries. */
  handles?: SeamHandle[],
): StitchHolePlacement[] {
  if (points.length < 2 || count < 1 || spacing <= 0) return [];
  const { segs, total } = buildSeamSegments(points, false, handles);
  if (segs.length === 0 || total <= 0) return [];
  const n = Math.floor(count);
  const lo = Math.max(0, Math.min(total, start));
  // The end-of-work-area handle, clamped to the seam. Left undefined the row
  // keeps its historical run-to-seam-end behavior (no windowing here).
  const hi = windowEnd != null ? Math.min(total, windowEnd) : null;
  const out: StitchHolePlacement[] = [];
  for (let i = 0; i < n; i++) {
    const d = lo + i * spacing;
    // Whether this hole's UN-INSET position (`i * spacing` from the seam origin)
    // still lands on the seam: a hole the follower's own length can hold, off the
    // working area only because a handle moved it there.
    const fits = i * spacing < total - 1e-6;
    // The work-area handle TRUNCATES the row: a hole past the END handle but still
    // ON the seam (#742 END handle), OR a *fitting* hole the START handle has
    // pushed past the seam end (#905 START handle), is outside the working area,
    // so it is simply not placed. The row keeps the main's pitch from the (moved)
    // start and the trailing hole disappears — no end-pinning, no squished final
    // gap. Genuine overflow (a hole `i * spacing` can't fit even from the origin)
    // is NOT dropped here; it falls through to the #687 clamp below.
    if (hi != null && d > hi + 1e-6 && (d <= total + 1e-6 || fits)) continue;
    // A hole whose pitch position runs past the seam END even from the origin is a
    // genuine count overflow (too many holes to fit at the main's pitch even
    // ignoring the inset): clamp it to the end and flag it non-conforming so the
    // pattern can ring the misfit (#687). This is independent of the work-area
    // handle.
    const pl: StitchHolePlacement = seamPlacementAt(segs, Math.min(total, d));
    if (d > total + 1e-6) pl.conforming = false;
    out.push(pl);
  }
  return out;
}

/** The center-to-center spacing a count-distributed seam ends up with — used to
 *  size the slant slot mark so it scales with the real spacing. */
function countSpacing(total: number, count: number, closed: boolean): number {
  const n = Math.max(1, Math.floor(count));
  return closed ? total / n : total / Math.max(1, n - 1);
}

const MM_PER_INCH = 25.4;

/** Default working-area inset from each end of a seam, in mm. An OPEN seam keeps
 *  a margin of half a stitch, but never more than 1/8"; a CLOSED loop defaults
 *  to 0 so the stitching runs continuously all the way around. */
export function defaultStitchInset(line: StitchLine): number {
  if (isStitchLoop(line)) return 0;
  return Math.min(effectivePitch(line) / 2, MM_PER_INCH / 8);
}

/** The resolved working-area insets `{ start, end }` (mm) — the line's stored
 *  values, or the end-dependent default when unset. */
export function resolveStitchInsets(line: StitchLine): {
  start: number;
  end: number;
} {
  const d = defaultStitchInset(line);
  return { start: line.startInset ?? d, end: line.endInset ?? d };
}

/** Absolute arc-length window `[lo, hi]` the holes are confined to, derived from
 *  the resolved insets and the seam length. `hi` is never less than `lo`. */
export function stitchWorkingWindow(
  line: StitchLine,
  total: number = stitchLineLength(line),
): { lo: number; hi: number } {
  const { start, end } = resolveStitchInsets(line);
  const lo = Math.max(0, Math.min(total, start));
  const hi = Math.max(lo, Math.min(total, total - end));
  return { lo, hi };
}

/** Arc-length positions (mm from the seam's first vertex) of the closed loop's
 *  cardinal extreme vertices — top (min y), bottom (max y), left (min x), right
 *  (max x). Used to drop alignment tick-marks on the closed-loop "start of path"
 *  slider. Returns null for an open seam or a degenerate loop. */
export function stitchCardinalTicks(
  line: StitchLine,
): { top: number; bottom: number; left: number; right: number } | null {
  if (line.closed !== true || line.points.length < 3) return null;
  let top = 0,
    bottom = 0,
    left = 0,
    right = 0;
  let minY = Infinity,
    maxY = -Infinity,
    minX = Infinity,
    maxX = -Infinity;
  let cum = 0;
  for (let i = 0; i < line.points.length; i++) {
    const p = line.points[i]!;
    if (p.y < minY) {
      minY = p.y;
      top = cum;
    }
    if (p.y > maxY) {
      maxY = p.y;
      bottom = cum;
    }
    if (p.x < minX) {
      minX = p.x;
      left = cum;
    }
    if (p.x > maxX) {
      maxX = p.x;
      right = cum;
    }
    const next = line.points[(i + 1) % line.points.length]!;
    cum += Math.hypot(next.x - p.x, next.y - p.y);
  }
  return { top, bottom, left, right };
}

/** The center-to-center spacing a seam's holes take WITHOUT its working-area
 *  window — count-driven (`total / count`, ring) when matched, else the pitch.
 *  Used to tell whether a closed loop's working area has grown large enough that
 *  its first and last holes meet, so it should stitch as a continuous ring. */
function unwindowedSpacing(line: StitchLine, total: number): number {
  if (line.holeCount != null && line.holeCount >= 1) {
    const count = holeCountAfterSkip(line.holeCount, effectiveSkipEvery(line));
    return countSpacing(total, count, true);
  }
  return effectivePitch(line);
}

/** True when the seam's holes should be confined to a working-area window —
 *  always for an open seam, and for a closed loop only while its insets carve
 *  out a real gap. Once the un-worked arc shrinks below one stitch spacing the
 *  loop's first and last holes would meet (overlap, double-count), so it
 *  stitches as a continuous ring instead and the holes redistribute evenly all
 *  the way around — recomputed live as the working area is dragged. */
export function isWindowed(line: StitchLine): boolean {
  if (!isStitchLoop(line)) return true;
  const total = stitchLineLength(line);
  const { lo, hi } = stitchWorkingWindow(line, total);
  const gap = total - (hi - lo);
  return gap >= unwindowedSpacing(line, total) - 1e-9;
}

/** Whether the seam's guide / hole-connector line should CLOSE back to the start
 *  — true only for a loop that stitches as one continuous ring. A closed loop
 *  whose working-area insets carve a real gap (#704) places its holes on an ARC,
 *  so its line is drawn OPEN: no closing segment joins the last hole to the
 *  first, matching where the holes actually sit. Mirrors the ring-vs-arc split
 *  in `stitchHolePlacements` so the drawn line and the punched holes never
 *  disagree. */
export function stitchRendersClosed(line: StitchLine): boolean {
  return isStitchLoop(line) && !isWindowed(line);
}

/** A view of the seam walked in its stored direction (#796). `"ccw"` reverses
 *  the point order so hole placement, the working-area window, and the slant
 *  tangents all run the other way. `"cw"` (the default) is the identity. The
 *  insets are DIRECTION-RELATIVE: `startInset` is always the margin at the end
 *  the stitching STARTS from, so the working area follows the stitch direction
 *  (reversing the points alone re-anchors the window to the new start). The
 *  stored geometry is never mutated; callers that place holes route through this
 *  so direction is honored in exactly one spot. */
export function directedStitchLine(line: StitchLine): StitchLine {
  if ((line.direction ?? "cw") !== "ccw") return line;
  return {
    ...line,
    points: [...line.points].reverse(),
    // The handles must turn around with the points, and BOTH things flip: the
    // order of the entries, and `in` / `out` within each — travelling the other
    // way makes what led into an anchor the thing that leads out of it. Reverse
    // the points alone and a curved seam quietly straightens (#982).
    handles: reverseSeamHandles(line.handles),
  };
}

/** All hole placements for a stitch line — the single source of truth shared by
 *  rendering, the exporters, the instruction step extractor, and `effective*`
 *  helpers. Resolves the pitch-vs-count branch, the working-area window, and the
 *  matched follower's skip. */
export function stitchHolePlacements(line: StitchLine): StitchHolePlacement[] {
  // Honor the seam's stored direction (#796) once, up front: for a "ccw" line
  // this hands the rest of the function a reversed-point view so the row runs
  // the other way (and its slant tangents flip) without any downstream change.
  line = directedStitchLine(line);
  // A loop (flagged closed OR an open polyline whose ends meet) stitches as a
  // continuous ring — count holes spread evenly around it, no end-pinning, no
  // wrap duplicate.
  const closed = isStitchLoop(line);
  const total = stitchLineLength(line);
  const off = line.startOffset ?? 0;
  const windowed = isWindowed(line);
  const win = windowed ? stitchWorkingWindow(line, total) : null;
  // A closed loop's working area is measured from the start-of-path origin
  // (`startOffset`), so shift the window by it and let the sampler wrap. An open
  // seam ignores `startOffset` — its working area pins the two ends absolutely.
  const window = win
    ? closed
      ? { start: off + win.lo, end: off + win.hi }
      : { start: win.lo, end: win.hi }
    : null;

  let placements: StitchHolePlacement[];
  if (line.holeCount != null && line.holeCount >= 1) {
    const count = holeCountAfterSkip(line.holeCount, effectiveSkipEvery(line));
    if (matchesMainPitch(line, closed, window)) {
      // "Match pitch exactly" (#199 default): lay the count at the main's pitch
      // from the working-area start so equal-length seams line up hole-for-hole
      // and the follower stays put when the main's working area changes. The
      // end handle (`window.end`) truncates the row so it also limits the end of
      // the work area (#742), matching how `window.start` limits the start.
      placements = samplePolylineHolesAtSpacing(
        line.points,
        count,
        line.matchSpacing!,
        window!.start,
        window!.end,
        line.handles,
      );
    } else {
      // Otherwise spread the count evenly to fill its own working area.
      placements = samplePolylineHolesByCount(
        line.points,
        count,
        closed,
        off,
        window,
        line.handles,
      );
      // A "fill working area" follower whose even spread doesn't land on the
      // main's pitch is OFF the matched grid — flag the whole row so the pattern
      // rings it (#687). (matchSpacing is only set on a matched follower.)
      if (line.matchSpacing != null && line.matchSpacing > 0) {
        const spread = effectiveStitchSpacing(line);
        if (Math.abs(spread - line.matchSpacing) > 1e-6) {
          for (const pl of placements) pl.conforming = false;
        }
      }
    }
  } else if (line.gridHoles != null) {
    // A follower of a corner-layout leader (#1107): the leader's holes aren't
    // on a pitch grid, so punch exactly the ones it handed down, windowed to
    // this seam's working area. Ahead of the follower's own corner layout on
    // purpose — an aligned seam must put its holes where its main does.
    placements = placeAtGridHoles(line, closed, total, off, window);
  } else if (usesCornerLayout(line)) {
    // Advanced layout (#691): pin a hole on every corner and on both ends of
    // the working area, then divide each run between pins evenly. Deliberately
    // BEFORE the construction grid below — a shared grid and a corner pin are
    // two different things asking to own the same hole, and the corner wins
    // because it's the one the user opted this seam into. Only a LEADER (or a
    // seam in no group) gets here: `usesCornerLayout` is false on a grid
    // follower, which punches its leader's holes instead (#1105).
    placements = samplePolylineHolesEvenCorners(
      line,
      closed,
      effectivePitch(line),
      window,
    );
  } else {
    const eff = effectivePitch(line);
    // Construction-anchored grid (#199 Phase 3): a seam traced from a
    // construction line snaps its holes to a grid on that line so two seams on
    // the same line at the same pitch line up where they overlap. Snap the
    // window start (open + inset-closed) or the ring rotation (full ring) to the
    // grid phase.
    let gOff = off;
    let gWindow = window;
    if (line.gridPhase != null && eff > 0) {
      if (gWindow) {
        gWindow = {
          start: gridWindowStart(line.gridPhase, gWindow.start, eff),
          end: gWindow.end,
        };
      } else {
        gOff = off + line.gridPhase;
      }
    }
    placements = samplePolylineHoles(
      line.points,
      eff,
      gOff,
      closed,
      gWindow,
      line.handles,
    );
  }
  // Main-side skip marks (#687): flag every Nth hole — the ones the follower
  // drops — so the renderer slashes them. `holeCountAfterSkip` removes by
  // skipping every Nth, so the same every-Nth hole on the main is the unpaired
  // one. Mains only (a follower carries `skipEvery`, never `skipMarkEvery`).
  const skipN = line.skipMarkEvery;
  if (skipN != null && skipN >= 2) {
    for (let i = 0; i < placements.length; i++) {
      if ((i + 1) % skipN === 0) placements[i]!.skip = true;
    }
  }
  markCornerHoles(line, closed, placements);
  return placements;
}

/** Place a follower's holes at the offsets its corner-layout leader handed down
 *  (#1107). `gridHoles` is measured from the RAW first point, so a reversed
 *  ("ccw") seam — already turned around by `directedStitchLine` — reads each one
 *  back from the other end.
 *
 *  An open seam keeps the holes inside its working area, in order along it. A
 *  loop keeps the ones on its arc (the window, which may wrap past the end) or
 *  all of them for a full ring, ordered from where the row starts — the window
 *  start, else `startOffset` — so the first hole is the one sewing begins at,
 *  as it is on the grid. */
function placeAtGridHoles(
  line: StitchLine,
  closed: boolean,
  total: number,
  off: number,
  window: { start: number; end: number } | null,
): StitchHolePlacement[] {
  const { segs } = buildSeamSegments(line.points, closed, line.handles);
  if (segs.length === 0 || !(total > 0)) return [];
  const reversed = (line.direction ?? "cw") === "ccw";
  // Where the raw first point sits in the directed frame: the far end of the
  // polyline. On a loop that is short of `total` by the closing run.
  const rawEnd = closed
    ? buildSeamSegments(line.points, false, line.handles).total
    : total;
  const directed = (d: number) =>
    !reversed ? d : closed ? modPos(rawEnd - d, total) : total - d;
  // The leader's pivot holes (#1105) are punched round there, so they are here.
  const pivots = line.gridCornerHoles ?? [];
  const isPivot = (raw: number) => pivots.some((c) => Math.abs(c - raw) < 1e-6);
  const offsets = (line.gridHoles ?? []).map((raw) => ({
    d: directed(raw),
    corner: isPivot(raw),
  }));
  const place = (d: number, corner: boolean): StitchHolePlacement => {
    const pl = seamPlacementAt(segs, d);
    return corner ? { ...pl, corner: true } : pl;
  };

  if (!closed) {
    const lo = window ? window.start : 0;
    const hi = window ? window.end : total;
    return offsets
      .filter((h) => h.d >= lo - 1e-6 && h.d <= hi + 1e-6)
      .sort((a, b) => a.d - b.d)
      .map((h) => place(Math.max(0, Math.min(total, h.d)), h.corner));
  }

  const from = window ? window.start : off;
  const span = window ? window.end - window.start : total;
  return offsets
    .map((h) => ({
      d: modPos(h.d, total),
      k: modPos(h.d - from, total),
      corner: h.corner,
    }))
    .filter((h) => h.k <= span + 1e-6)
    .sort((a, b) => a.k - b.k)
    .map((h) => place(h.d, h.corner));
}

/**
 * Flag the PIVOT holes — the ones the corner-aware layout pinned on the seam's
 * corners (#691) — so the renderers punch them round instead of with the row's
 * own chisel.
 *
 * Pivot holes are `usesCornerLayout`'s doing, not this function's: without it
 * there is no hole on a corner to reshape, and rounding whichever hole happened
 * to land nearest would reinforce one beside the turn while the turn itself
 * fell in the middle of a gap. So this only asks which of the existing holes
 * are the pivots.
 *
 * The tolerance is a float epsilon, not a search radius. A corner with no hole
 * on it — one outside the working window, or one whose holes the part outline
 * cropped away — simply has no pivot hole, which is the truth.
 */
function markCornerHoles(
  line: StitchLine,
  closed: boolean,
  placements: StitchHolePlacement[],
): void {
  if (line.roundCornerHoles === false || placements.length === 0) return;
  if (!usesCornerLayout(line)) return;
  const corners = seamCornerIndices(
    line.points,
    closed,
    line.handles,
    effectiveCornerAngle(line),
  );
  for (const ci of corners) {
    const c = line.points[ci];
    if (!c) continue;
    for (const pl of placements) {
      if (Math.hypot(pl.position.x - c.x, pl.position.y - c.y) < 1e-6) {
        pl.corner = true;
        break;
      }
    }
  }
}

/** Whether a count-carrying seam should lay its holes at its matched main's
 *  pitch (`matchSpacing`) rather than spreading to fill — the "Match pitch
 *  exactly" mode (#199). Needs an open windowed seam, a stamped `matchSpacing`,
 *  and the user not having opted into `fillWorkingArea`. */
function matchesMainPitch(
  line: StitchLine,
  closed: boolean,
  window: { start: number; end: number } | null,
): boolean {
  return (
    !closed &&
    window != null &&
    !line.fillWorkingArea &&
    line.matchSpacing != null &&
    line.matchSpacing > 0
  );
}

/** How many holes a matched follower keeps after "Skip every Nth" (#199).
 *  Skipping doesn't punch a gap at the Nth position — it removes
 *  `floor(count / skipEvery)` holes from the total and the survivors re-spread
 *  EVENLY along the seam, so the row stays uniform with fewer, wider stitches. */
export function holeCountAfterSkip(count: number, skipEvery?: number): number {
  const n = Math.max(0, Math.floor(count));
  if (skipEvery == null || skipEvery < 2 || n < 1) return n;
  return Math.max(1, n - Math.floor(n / skipEvery));
}

/** The skip cadence actually IN EFFECT for a seam, or `undefined` when it isn't
 *  skipping (#199). Skipping applies only when the `skipStitches` toggle is
 *  explicitly on AND the stored `skipEvery` is a valid `>= 2`. This is the single
 *  source of truth for the numeric value; `skipsStitches` is the boolean view. */
export function effectiveSkipEvery(
  line: Pick<StitchLine, "skipStitches" | "skipEvery">,
): number | undefined {
  const n = line.skipEvery;
  return line.skipStitches === true && n != null && n >= 2 ? n : undefined;
}

/** Whether a seam skips stitches at all — the boolean to use wherever only the
 *  fact of skipping matters (e.g. instructions), not the value (#199). */
export function skipsStitches(
  line: Pick<StitchLine, "skipStitches" | "skipEvery">,
): boolean {
  return effectiveSkipEvery(line) != null;
}

/** Largest legal "skip every Nth" for a seam of `holeCount` holes: `floor(count
 *  / 2)` (so at least half the row survives), or `0` when the seam is too short
 *  to skip at all (fewer than 4 holes). The UI clamps the number field to
 *  `2 … maxSkipEvery`. */
export function maxSkipEvery(holeCount: number): number {
  const half = Math.floor(Math.max(0, holeCount) / 2);
  return half >= 2 ? half : 0;
}

/** The "skip every Nth" whose resulting stitch spacing lands CLOSEST to the
 *  matched main's pitch (#199) — the sensible default when the user first turns
 *  skipping on (thin a denser follower back toward the main's spacing). Searches
 *  `2 … maxSkipEvery`; falls back to `2` when the seam is too short or isn't
 *  matched to a pitch.
 *
 *  The probe measures the follower's holes SPREAD across its working area (as if
 *  filling), independent of the "match pitch exactly" toggle. That toggle would
 *  otherwise pin the rendered pitch to the main's for every N — making the search
 *  degenerate — but the meaningful question is still "how many holes match the
 *  main's density over this seam's length", which is the fill spacing. */
export function defaultSkipEvery(line: StitchLine): number {
  const max = maxSkipEvery(line.holeCount ?? 0);
  if (max < 2) return 2;
  const target = line.matchSpacing;
  if (target == null || target <= 0) return 2;
  let bestN = 2;
  let bestErr = Infinity;
  for (let n = 2; n <= max; n++) {
    const spacing = effectiveStitchSpacing({
      ...line,
      fillWorkingArea: true,
      skipStitches: true,
      skipEvery: n,
    });
    const err = Math.abs(spacing - target);
    if (err < bestErr - 1e-9) {
      bestErr = err;
      bestN = n;
    }
  }
  return bestN;
}

/**
 * Build SVG path data for a single stitch hole. Round = circle approximated
 * by two arcs; slant = thin oblique slot (capsule); diamond = rotated rhombus.
 *
 * `size` is the long-axis dimension in mm. For slant/diamond the short axis
 * is derived from a fixed aspect ratio so the user only has to pick one
 * number.
 */
export function stitchHolePathData(
  placement: StitchHolePlacement,
  shape: StitchShape,
  size: number,
  slantAngle: number,
  /** Center-to-center pitch in mm — only consulted for slant chisels,
   *  where the slot's short axis (thickness) scales with the spacing. */
  pitch?: number,
): string {
  const { position, tangentDeg } = placement;
  const cx = position.x;
  const cy = position.y;
  if (shape === "round") {
    const r = size / 2;
    // SVG circle as two 180° arcs — works inside a CompoundPath for boolean
    // subtraction during leather clipping.
    return (
      `M ${cx - r} ${cy} ` +
      `A ${r} ${r} 0 0 0 ${cx + r} ${cy} ` +
      `A ${r} ${r} 0 0 0 ${cx - r} ${cy} Z`
    );
  }
  if (shape === "diamond") {
    // Rhombus with long diagonal = `size`, short diagonal = `size / 3`
    // (typical Japanese chisel proportion). Oriented so two opposite
    // sides are parallel to the seam direction — the long diagonal sits
    // at α/2 from the seam where α is the interior angle. Mathematically
    // the four vertices, in a seam-aligned local frame, sit at:
    //   (-q, -h), (p, -h), (q, h), (-p, h)
    // with p = W²/s, q = L²/s, h = L·W/s, s = √(L² + W²).
    //
    // `slantAngle` doubles as a direction toggle — its magnitude doesn't
    // change the geometry (the rotation is fixed by L/W), but the sign
    // mirrors the rhombus across the seam. Negative = "regular" (long
    // diagonal slopes up-right, matching slant chisels' regular 45°);
    // non-negative = "inverse".
    const L = size / 2;
    const W = size / 6;
    const sLen = Math.sqrt(L * L + W * W);
    const p = (W * W) / sLen;
    const q = (L * L) / sLen;
    const hMag = (L * W) / sLen;
    const h = slantAngle < 0 ? -hMag : hMag;
    const rad = (tangentDeg * Math.PI) / 180;
    const cosT = Math.cos(rad);
    const sinT = Math.sin(rad);
    const project = (lx: number, ly: number) => ({
      x: cx + lx * cosT - ly * sinT,
      y: cy + lx * sinT + ly * cosT,
    });
    const v0 = project(-q, -h);
    const v1 = project(p, -h);
    const v2 = project(q, h);
    const v3 = project(-p, h);
    return (
      `M ${v0.x} ${v0.y} L ${v1.x} ${v1.y} ` +
      `L ${v2.x} ${v2.y} L ${v3.x} ${v3.y} Z`
    );
  }
  // shape === "slant"
  // Flat-ended oblique slit. `size` is the *tooth width* — the perpendicular
  // distance between the outer and inner ends of the slot when measured
  // across the seam direction. The slot's long axis stretches by
  // 1/|sin(slantAngle)| so a 45° iron with a 1.8 mm tooth width punches a
  // ≈ 2.55 mm slit. Short axis (slot thickness) scales with the seam pitch
  // — 0.1 mm at fine spacing, 0.3 mm at wide spacing — so coarse irons
  // read chunkier than tight ones, matching real pricking-iron prongs.
  const slantSinAbs = Math.abs(Math.sin((slantAngle * Math.PI) / 180));
  // Guard for ~0° (slot lying along the seam). Falling back to size avoids a
  // divide-by-zero blow-up; the resulting slot is just `size` long.
  const slotLength = slantSinAbs > 0.05 ? size / slantSinAbs : size;
  const halfL = slotLength / 2;
  const halfW = slantSlotThicknessForPitch(pitch ?? 3.38) / 2;
  const totalDeg = tangentDeg + slantAngle;
  const rad = (totalDeg * Math.PI) / 180;
  const ux = Math.cos(rad);
  const uy = Math.sin(rad);
  // Perpendicular (CCW in y-down).
  const px = -uy;
  const py = ux;
  const start = { x: cx - ux * halfL, y: cy - uy * halfL };
  const end = { x: cx + ux * halfL, y: cy + uy * halfL };
  const tl = { x: start.x + px * halfW, y: start.y + py * halfW };
  const bl = { x: start.x - px * halfW, y: start.y - py * halfW };
  const tr = { x: end.x + px * halfW, y: end.y + py * halfW };
  const br = { x: end.x - px * halfW, y: end.y - py * halfW };
  return (
    `M ${tl.x} ${tl.y} L ${tr.x} ${tr.y} ` +
    `L ${br.x} ${br.y} L ${bl.x} ${bl.y} Z`
  );
}

/** All hole path-data strings for a stitch line, derived from its parameters.
 *  Returned as separate strings so the renderer can group them into a
 *  CompoundPath or iterate them as individual children. */
/** One renderable hole mark: its chisel path, its center, and whether it sits
 *  on the matched pitch (#687). The single source for every consumer that draws
 *  the off-pitch wavy connector (canvas, SVG / PDF). */
export interface StitchHoleMark {
  /** SVG path data for the chisel footprint. */
  d: string;
  /** Hole center (for the off-pitch wavy connector). */
  position: Vec2;
  /** False when the hole is off the matched main's pitch (its connector is drawn
   *  wavy on the pattern); true / normal otherwise. */
  conforming: boolean;
  /** SVG path for a short diagonal slash through this hole — present only on a
   *  main hole to be SKIPPED when sewing (#687); absent otherwise. */
  skipD?: string;
  /** SVG path for the START / SHARED-HOLE marker — a ring round the hole plus a
   *  chevron pointing the way the row runs (#693). On a matched seam it marks
   *  the first hole; on a grid-aligned one, a hole shared with another part.
   *  Absent otherwise. */
  startD?: string;
  /** The letter naming a SHARED hole's pair (#693) — the same letter sits on the
   *  partner part's matching hole, and the assembly steps name it. Present only
   *  on a shared hole. */
  pairLabel?: string;
  /** SVG path drawing `pairLabel` beside the ring. */
  pairLabelD?: string;
}

/**
 * Whether this seam should mark where its stitching STARTS (#693) — a MATCHED
 * seam only.
 *
 * A match pairs two rows that share a hole COUNT, so they run off hole-for-hole
 * from their two starts: marking each start IS marking the correspondence, and
 * ring meets ring.
 *
 * GRID-aligned seams (#199 Phase 3) are deliberately NOT marked here even
 * though they are equally "sewn together". They share hole POSITIONS on one
 * construction line and are clipped to their own parts, so their rows generally
 * begin at different points along it — two first holes that are not partners.
 * Ringing them would invite you to line up the wrong pair. What those seams get
 * instead is a labelled mark on the holes they actually SHARE — see
 * `stitchPairMarks.ts`.
 */
export function marksStartHole(line: StitchLine): boolean {
  return line.matchId != null;
}

/**
 * Stencil capitals, as unit strokes in a 0.62 × 1 box with the origin top-left.
 *
 * A stroke alphabet rather than real text on purpose: these glyphs are plain
 * path data so the canvas, the SVG and the PDF draw the identical mark — the
 * same reason the ring and the skip slash are paths. `<text>` would put the
 * pattern at the mercy of whatever font the PDF writer resolves, for a label
 * whose whole job is to be unambiguous.
 */
const STENCIL: Record<string, string> = {
  A: "M0,1 L0.31,0 L0.62,1 M0.13,0.6 L0.49,0.6",
  B: "M0,0 L0,1 M0,0 L0.45,0 L0.62,0.22 L0.45,0.45 L0,0.45 M0,0.45 L0.5,0.45 L0.62,0.72 L0.45,1 L0,1",
  C: "M0.62,0.2 L0.45,0 L0.17,0 L0,0.2 L0,0.8 L0.17,1 L0.45,1 L0.62,0.8",
  D: "M0,0 L0,1 M0,0 L0.4,0 L0.62,0.25 L0.62,0.75 L0.4,1 L0,1",
  E: "M0.62,0 L0,0 L0,1 L0.62,1 M0,0.48 L0.48,0.48",
  F: "M0.62,0 L0,0 L0,1 M0,0.48 L0.45,0.48",
  G: "M0.62,0.2 L0.45,0 L0.17,0 L0,0.2 L0,0.8 L0.17,1 L0.45,1 L0.62,0.8 L0.62,0.55 L0.35,0.55",
  H: "M0,0 L0,1 M0.62,0 L0.62,1 M0,0.48 L0.62,0.48",
  I: "M0.1,0 L0.52,0 M0.31,0 L0.31,1 M0.1,1 L0.52,1",
  J: "M0.62,0 L0.62,0.78 L0.45,1 L0.17,1 L0,0.8",
  K: "M0,0 L0,1 M0.62,0 L0,0.52 M0.2,0.38 L0.62,1",
  L: "M0,0 L0,1 L0.62,1",
  M: "M0,1 L0,0 L0.31,0.45 L0.62,0 L0.62,1",
  N: "M0,1 L0,0 L0.62,1 L0.62,0",
  O: "M0.17,0 L0.45,0 L0.62,0.2 L0.62,0.8 L0.45,1 L0.17,1 L0,0.8 L0,0.2 Z",
  P: "M0,1 L0,0 L0.45,0 L0.62,0.22 L0.45,0.5 L0,0.5",
  Q: "M0.17,0 L0.45,0 L0.62,0.2 L0.62,0.8 L0.45,1 L0.17,1 L0,0.8 L0,0.2 Z M0.35,0.7 L0.62,1",
  R: "M0,1 L0,0 L0.45,0 L0.62,0.22 L0.45,0.5 L0,0.5 M0.3,0.5 L0.62,1",
  S: "M0.62,0.2 L0.45,0 L0.17,0 L0,0.2 L0.17,0.45 L0.45,0.5 L0.62,0.75 L0.45,1 L0.17,1 L0,0.8",
  T: "M0,0 L0.62,0 M0.31,0 L0.31,1",
  U: "M0,0 L0,0.8 L0.17,1 L0.45,1 L0.62,0.8 L0.62,0",
  V: "M0,0 L0.31,1 L0.62,0",
  W: "M0,0 L0.13,1 L0.31,0.4 L0.49,1 L0.62,0",
  X: "M0,0 L0.62,1 M0.62,0 L0,1",
  Y: "M0,0 L0.31,0.5 L0.62,0 M0.31,0.5 L0.31,1",
  Z: "M0,0 L0.62,0 L0,1 L0.62,1",
};

/** Cap height (mm) of a joint label, and its floor so a fine chisel still gets a
 *  readable letter. Kept close to the chevron's own reach: the pair is one mark,
 *  and a letter that dwarfs its pointer is back to occupying the row. */
const LABEL_HEIGHT_SCALE = 2;
const LABEL_HEIGHT_MIN_MM = 2.3;

/**
 * The letter(s) naming a shared-hole pair (#693), set beside the hole's ring.
 *
 * Drawn UPRIGHT rather than rotated onto the seam: the pattern is printed in
 * this space, so axis-aligned is the way round the reader holds it, and a label
 * tumbling with the row would be upside down on half of a closed seam. Offset
 * perpendicular to the row so it clears the ring and the chevron.
 */
export function pairLabelPath(
  label: string,
  pos: Vec2,
  tangentDeg: number,
  size: number,
): string {
  const h = Math.max(size * LABEL_HEIGHT_SCALE, LABEL_HEIGHT_MIN_MM);
  const w = h * 0.62;
  const gap = w * 0.28;
  const chars = [...label.toUpperCase()].filter((c) => STENCIL[c]);
  if (chars.length === 0) return "";
  const totalW = chars.length * w + (chars.length - 1) * gap;
  // Beyond the chevron, on the same side, so the pointer and its letter read as
  // one mark: hole ← chevron ← letter.
  const a = (tangentDeg * Math.PI) / 180;
  const nx = -Math.sin(a);
  const ny = Math.cos(a);
  const reach = markReach(size) + h * 0.6;
  const originX = pos.x + nx * reach - totalW / 2;
  const originY = pos.y + ny * reach - h / 2;
  const n = (v: number) => Math.round(v * 1000) / 1000;
  const out: string[] = [];
  chars.forEach((c, i) => {
    const dx = originX + i * (w + gap);
    // Rewrite the unit strokes into place. Both axes scale by the CAP HEIGHT:
    // the unit box is already 0.62 wide, so scaling x by the box width would
    // apply that factor twice and squash the letter.
    out.push(
      STENCIL[c]!.replace(
        /(-?[\d.]+),(-?[\d.]+)/g,
        (_m, ux: string, uy: string) =>
          `${n(dx + parseFloat(ux) * h)} ${n(originY + parseFloat(uy) * h)}`,
      ),
    );
  });
  return out.join(" ");
}

/** Clearance (mm) between the hole's centre and the chevron's tip, so the tip
 *  reads as pointing AT the hole rather than touching it. */
const MARK_TIP_GAP_SCALE = 0.95;
const MARK_TIP_GAP_MIN_MM = 1;
/** Arm length (mm) of the chevron. */
const MARK_ARM_SCALE = 1;
const MARK_ARM_MIN_MM = 1.2;
/** Half-angle of the V. Wide enough to read as a pointer, narrow enough not to
 *  sprawl along the row into its neighbours. */
const MARK_SPREAD_DEG = 38;

/** How far the marker reaches from the hole it points at — the label sits
 *  beyond this, so the two are spaced off one number. */
function markReach(size: number): number {
  const gap = Math.max(size * MARK_TIP_GAP_SCALE, MARK_TIP_GAP_MIN_MM);
  const arm = Math.max(size * MARK_ARM_SCALE, MARK_ARM_MIN_MM);
  return gap + arm * Math.cos((MARK_SPREAD_DEG * Math.PI) / 180);
}

/**
 * The hole marker: a chevron pointing AT the hole, from beside the row.
 *
 * Off to the side rather than around the hole, because a ring centred on a hole
 * competes with the thing it is marking — at chisel sizes it reads as a bigger
 * hole, and on a dense row it swallows its neighbours. A pointer says "this
 * one" without covering it, and takes a fraction of the space.
 *
 * The tip is what carries the meaning, so it sits a clear gap short of the hole
 * and the V opens away: the eye follows the arms in to the point, and the point
 * is unambiguous even where holes are a couple of millimetres apart.
 *
 * Built as plain path data (this module is paper-free) so the canvas renderer
 * and the exporters draw the identical glyph.
 */
export function startMarkerPath(
  pos: Vec2,
  tangentDeg: number,
  size: number,
): string {
  const gap = Math.max(size * MARK_TIP_GAP_SCALE, MARK_TIP_GAP_MIN_MM);
  const arm = Math.max(size * MARK_ARM_SCALE, MARK_ARM_MIN_MM);
  const n = (v: number) => Math.round(v * 1000) / 1000;
  // Perpendicular to the row: along it, the chevron would sit on the next hole.
  const a = (tangentDeg * Math.PI) / 180;
  const nx = -Math.sin(a);
  const ny = Math.cos(a);
  const tipX = pos.x + nx * gap;
  const tipY = pos.y + ny * gap;
  // The arms run back OUT from the tip, splayed either side of the normal.
  const spread = (MARK_SPREAD_DEG * Math.PI) / 180;
  const armEnd = (sign: 1 | -1) => {
    const c = Math.cos(sign * spread);
    const s = Math.sin(sign * spread);
    return {
      x: tipX + (nx * c - ny * s) * arm,
      y: tipY + (nx * s + ny * c) * arm,
    };
  };
  const e1 = armEnd(1);
  const e2 = armEnd(-1);
  return `M${n(e1.x)} ${n(e1.y)} L${n(tipX)} ${n(tipY)} L${n(e2.x)} ${n(e2.y)}`;
}

/** Turn an ALREADY-RESOLVED placement list into renderable hole marks — the tail
 *  of `stitchHoleMarks` split out (#910) so a consumer that has filtered the
 *  placements (e.g. to the leather + work-area window via `placedStitchHoles`)
 *  can build marks WITHOUT re-running the Layer-A sampler. `stitchHoleMarks`
 *  itself is now `marksFromPlacements(line, stitchHolePlacements(line))` — same
 *  output as before. Paper-free. */
export function marksFromPlacements(
  line: StitchLine,
  placements: StitchHolePlacement[],
  /** Shared-hole marks for THIS seam (#693) — `{ index, label }` into the same
   *  list handed in, from `pairMarksByStitch`. Indices that fall outside it are
   *  ignored rather than clamped: a hole that isn't punched has no partner. */
  pairs?: ReadonlyArray<{ index: number; label: string }>,
): StitchHoleMark[] {
  const markPitch = effectiveStitchSpacing(line);
  // The start marker goes on the FIRST mark of the list handed in, not on
  // placement 0 of the raw row (#693): a seam overhanging its part is filtered
  // to what lands on the leather, and the hole you start at is the first one
  // actually punched.
  const start = marksStartHole(line);
  const labelAt = new Map<number, string>();
  for (const p of pairs ?? []) {
    if (p.index >= 0 && p.index < placements.length)
      labelAt.set(p.index, p.label);
  }
  return placements.map((pl, i) => {
    const label = labelAt.get(i);
    // A corner hole is punched ROUND (#691). `size` already means the
    // across-seam tooth width for slant and diamond, so the round hole spans
    // what the slot it replaces did — clamped only to guard a stored value
    // outside the round shape's own range.
    const round = pl.corner === true && line.shape !== "round";
    return {
      d: stitchHolePathData(
        pl,
        round ? "round" : line.shape,
        round ? clampStitchSize("round", line.size) : line.size,
        line.slantAngle,
        markPitch,
      ),
      position: pl.position,
      conforming: pl.conforming !== false,
      ...(pl.skip
        ? { skipD: skipSlashPath(pl.position, pl.tangentDeg, line.size) }
        : {}),
      ...(start && i === 0
        ? { startD: startMarkerPath(pl.position, pl.tangentDeg, line.size) }
        : {}),
      // A shared hole gets the same ring, plus the letter that names its
      // partner. Ringed with `startD` so one renderer branch draws both — they
      // are the same glyph doing the same job at different scopes.
      ...(label
        ? {
            startD: startMarkerPath(pl.position, pl.tangentDeg, line.size),
            pairLabel: label,
            pairLabelD: pairLabelPath(
              label,
              pl.position,
              pl.tangentDeg,
              line.size,
            ),
          }
        : {}),
    };
  });
}

/** Whether any mark carries a start marker — lets a renderer skip the work on
 *  the overwhelmingly common unmatched seam (#693). */
export function hasStartMarker(marks: ReadonlyArray<StitchHoleMark>): boolean {
  return marks.some((m) => m.startD != null);
}

export function stitchHoleMarks(line: StitchLine): StitchHoleMark[] {
  return marksFromPlacements(line, stitchHolePlacements(line));
}

/** Whether any mark carries a skip slash — lets a renderer skip the work when a
 *  seam has no main-side skip marks (#687). */
export function hasSkipMarks(marks: StitchHoleMark[]): boolean {
  return marks.some((m) => m.skipD != null);
}

/** A short diagonal slash centered on a skipped hole, angled across the seam
 *  (tangent + 60°) so it reads as "cross this one out" (#687). Length scales with
 *  the hole so it stays legible at any size. */
function skipSlashPath(pos: Vec2, tangentDeg: number, size: number): string {
  const len = Math.max(size * 1.8, 2.2) * 0.7;
  const a = ((tangentDeg + 60) * Math.PI) / 180;
  const hx = (Math.cos(a) * len) / 2;
  const hy = (Math.sin(a) * len) / 2;
  const r = (n: number) => Math.round(n * 1000) / 1000;
  return `M${r(pos.x - hx)} ${r(pos.y - hy)} L${r(pos.x + hx)} ${r(pos.y + hy)}`;
}

/** The path-data tail of `stitchHolePaths`, over an already-resolved placement
 *  list (#910) — mirrors `marksFromPlacements` so a consumer with pre-filtered
 *  placements skips re-running Layer A. Identical output to the old inline map. */
export function pathsFromPlacements(
  line: StitchLine,
  placements: StitchHolePlacement[],
): string[] {
  return marksFromPlacements(line, placements).map((m) => m.d);
}

export function stitchHolePaths(line: StitchLine): string[] {
  return pathsFromPlacements(line, stitchHolePlacements(line));
}

/** Amplitude (mm) of the off-pitch wave when no target length is known — kept
 *  tiny so it reads as a subtle ripple, not a zigzag. */
const OFF_PITCH_WAVE_AMPLITUDE = 0.3;
/** Wavelength (mm) of the off-pitch wave — long enough to stay gentle. */
const OFF_PITCH_WAVE_LENGTH = 6;

/** Sampled arc length of the `cycles`-cycle sine `A·sin(2π·cycles·t)` drawn
 *  across a straight chord of length `chord` (t ∈ [0, 1]), evaluated at the same
 *  `steps` the connector draws with so the measured length matches the rendered
 *  one exactly. Amplitude-only (orientation-independent), so it's solved in
 *  chord space and reused for the world-space points. */
function sineChordLength(
  chord: number,
  amp: number,
  cycles: number,
  steps: number,
): number {
  let len = 0;
  let prevAlong = 0;
  let prevOff = 0;
  for (let s = 1; s <= steps; s++) {
    const t = s / steps;
    const along = t * chord;
    const off = amp * Math.sin(t * cycles * 2 * Math.PI);
    len += Math.hypot(along - prevAlong, off - prevOff);
    prevAlong = along;
    prevOff = off;
  }
  return len;
}

/** The wave amplitude (mm) that stretches a `cycles`-cycle sine over `chord` mm
 *  until its arc length equals `target` mm — so an off-pitch follower gap that's
 *  been SQUISHED below the main's stitch length bulges just enough to carry a
 *  full main stitch's worth of thread (#702). Returns 0 when the gap is already
 *  at or past the target (a straight line can't be made shorter). Amplitude is
 *  bisected and capped at `target` so a nearly-collapsed gap bulges hard instead
 *  of running away to a spike. */
export function offPitchWaveAmplitude(
  chord: number,
  cycles: number,
  steps: number,
  target: number,
): number {
  if (!(target > 0) || !(chord >= 0) || target <= chord) return 0;
  const cap = target;
  if (sineChordLength(chord, cap, cycles, steps) <= target) return cap;
  let lo = 0;
  let hi = cap;
  for (let i = 0; i < 48; i++) {
    const mid = (lo + hi) / 2;
    if (sineChordLength(chord, mid, cycles, steps) < target) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/** True when any of a seam's holes is off its matched main's pitch (#687). */
export function hasOffPitchHoles(
  marks: ReadonlyArray<StitchHoleMark>,
): boolean {
  return marks.some((m) => !m.conforming);
}

/**
 * The selected seam's line, split by how each gap compares with the row's
 * uniform pitch (#691) — so the drawing says WHERE the spacing is off and which
 * way, instead of looking identical to an even row.
 *
 * Three path-data strings, any of which may be null:
 *
 *  - `even` — gaps at the pitch. Drawn as the ordinary selection line.
 *  - `compressed` — gaps SHORTER than the pitch. A sine wave, the same idiom a
 *    matched follower's over-tight stitches already use (#687): more line than
 *    the straight distance, which is what a squeezed stitch is.
 *  - `stretched` — gaps LONGER than the pitch. Left straight for the renderer to
 *    draw THINNER, since a line pulled longer is a line pulled thinner. A wave
 *    would be wrong here — it would read as more thread, not less.
 *
 * Each is a set of independent `M…L…` subpaths, one per gap, so a renderer can
 * give the three different stroke weights without walking the row itself.
 *
 * `tolerance` is absolute millimetres. Gaps are compared against `pitch`, not
 * against each other: a row whose every gap is 4.3 mm on a 4 mm iron is wrong
 * everywhere, and should look wrong everywhere.
 */
export function stitchPitchDeviationPaths(
  marks: ReadonlyArray<StitchHoleMark>,
  closed: boolean,
  pitch: number,
  tolerance = 0.01,
): {
  even: string | null;
  compressed: string | null;
  stretched: string | null;
} {
  const none = { even: null, compressed: null, stretched: null };
  if (marks.length < 2 || !(pitch > 0)) return none;
  const r = (n: number) => Math.round(n * 1000) / 1000;
  const even: string[] = [];
  const compressed: string[] = [];
  const stretched: string[] = [];

  const addGap = (a: Vec2, b: Vec2) => {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return;
    const move = `M${r(a.x)} ${r(a.y)}`;
    if (Math.abs(len - pitch) <= tolerance) {
      even.push(`${move}L${r(b.x)} ${r(b.y)}`);
      return;
    }
    if (len > pitch) {
      stretched.push(`${move}L${r(b.x)} ${r(b.y)}`);
      return;
    }
    // Shorter than the pitch: bulge a wave whose ARC LENGTH is the pitch, so the
    // line carries exactly the thread the gap is missing.
    const ux = dx / len;
    const uy = dy / len;
    const nx = -uy;
    const ny = ux;
    const cycles = Math.max(1, Math.round(len / OFF_PITCH_WAVE_LENGTH));
    const steps = Math.max(8, cycles * 8);
    const amp = offPitchWaveAmplitude(len, cycles, steps, pitch);
    const pts = [move];
    for (let s = 1; s <= steps; s++) {
      const t = s / steps;
      const along = t * len;
      const off = amp * Math.sin(t * cycles * 2 * Math.PI);
      pts.push(
        `L${r(a.x + ux * along + nx * off)} ${r(a.y + uy * along + ny * off)}`,
      );
    }
    compressed.push(pts.join(""));
  };

  for (let i = 0; i + 1 < marks.length; i++)
    addGap(marks[i]!.position, marks[i + 1]!.position);
  if (closed) addGap(marks[marks.length - 1]!.position, marks[0]!.position);

  return {
    even: even.length ? even.join("") : null,
    compressed: compressed.length ? compressed.join("") : null,
    stretched: stretched.length ? stretched.join("") : null,
  };
}

/** SVG path data for the connector THROUGH a seam's holes (#687): straight
 *  between on-pitch holes, but a gentle sine WAVE across any gap where a hole is
 *  off the matched pitch — so the seam line itself reads "the stitches here
 *  should be tighter than they look" without an extra overlay. For a `closed`
 *  loop the closing gap (last hole → first) is included and the path is closed.
 *  Returns null for fewer than two holes. Used as the seam's selection line on
 *  the canvas and its seam line in the PDF, replacing the straight one. */
export function stitchConnectorPath(
  marks: ReadonlyArray<StitchHoleMark>,
  closed = false,
  /** The main stitch length (mm) — the follower's inherited `matchSpacing`. When
   *  given, each off-pitch gap's wave is amplified so its ARC LENGTH equals this
   *  (#702): a gap squished below one main stitch bulges to carry a full stitch's
   *  worth of thread. When omitted, a subtle fixed-amplitude ripple is drawn. */
  targetSegmentLength?: number,
): string | null {
  if (marks.length < 2) return null;
  const wavelength = OFF_PITCH_WAVE_LENGTH;
  const r = (n: number) => Math.round(n * 1000) / 1000;
  const parts = [`M${r(marks[0]!.position.x)} ${r(marks[0]!.position.y)}`];
  // Append the gap from `a` to `b`: a straight `L b` on-pitch, else wave points.
  const gap = (a: StitchHoleMark, b: StitchHoleMark) => {
    const dx = b.position.x - a.position.x;
    const dy = b.position.y - a.position.y;
    const len = Math.hypot(dx, dy);
    const straight = (a.conforming && b.conforming) || len < 1e-3;
    if (straight) {
      parts.push(`L${r(b.position.x)} ${r(b.position.y)}`);
      return;
    }
    const ux = dx / len;
    const uy = dy / len;
    const nx = -uy; // unit normal (wave swings perpendicular to the seam)
    const ny = ux;
    const cycles = Math.max(1, Math.round(len / wavelength));
    const steps = Math.max(8, cycles * 8);
    // Bulge the wave until its arc length equals one main stitch (#702); fall
    // back to the fixed subtle ripple when no target length is supplied.
    const amp =
      targetSegmentLength != null && targetSegmentLength > 0
        ? offPitchWaveAmplitude(len, cycles, steps, targetSegmentLength)
        : OFF_PITCH_WAVE_AMPLITUDE;
    for (let s = 1; s <= steps; s++) {
      const t = s / steps;
      const along = t * len;
      const off = amp * Math.sin(t * cycles * 2 * Math.PI);
      parts.push(
        `L${r(a.position.x + ux * along + nx * off)} ${r(a.position.y + uy * along + ny * off)}`,
      );
    }
  };
  for (let i = 0; i + 1 < marks.length; i++) gap(marks[i]!, marks[i + 1]!);
  if (closed && marks.length > 1) {
    gap(marks[marks.length - 1]!, marks[0]!);
    parts.push("Z");
  }
  return parts.join(" ");
}

/** The center-to-center spacing the seam currently renders at — count-based
 *  spacing across its working area when matched (#199), else the effective
 *  pitch. Used to size the slant slot mark. */
export function effectiveStitchSpacing(line: StitchLine): number {
  if (line.holeCount != null && line.holeCount >= 1) {
    const count = holeCountAfterSkip(line.holeCount, effectiveSkipEvery(line));
    const total = stitchLineLength(line);
    const loop = isStitchLoop(line);
    if (isWindowed(line)) {
      const { lo, hi } = stitchWorkingWindow(line, total);
      const window = { start: lo, end: hi };
      // "Match pitch exactly" follower: spacing IS the main's pitch.
      if (matchesMainPitch(line, loop, window)) return line.matchSpacing!;
      return countSpacing(hi - lo, count, false);
    }
    return countSpacing(total, count, loop);
  }
  return effectivePitch(line);
}

/** The PRE-outline-filter hole count of a seam: its fixed `holeCount` when
 *  matched (#199), else the pitch-based count within its working area. This is
 *  the along-seam / working-window count BEFORE the on-piece leather retention
 *  filter (#910) — it deliberately stays paper-free and outline-agnostic so it
 *  can be used where the piece outline must NOT matter: seeding a new match's
 *  shared count from the denser of the two seams (`stitchMatch.ts`) and the
 *  closed-loop ring math in this module. The human-facing "holes on the leather"
 *  count that render / export / the panel / instructions show routes through
 *  `placedStitchHoles(line, piece, scope).length` instead, which drops the holes
 *  that overflow the piece; the split is intentional, not drift. */
export function effectiveHoleCount(line: StitchLine): number {
  if (line.holeCount != null && line.holeCount >= 1) {
    return Math.floor(line.holeCount);
  }
  return stitchHolePlacements(line).length;
}

/** Apply a stitching-defaults block to one line. Used by the cascade-on-write
 *  path so non-overriding lines stay in sync when a higher level changes. */
export function applyStitchingDefaultsToLine(
  line: StitchLine,
  d: StitchingDefaults,
): StitchLine {
  return {
    ...line,
    shape: d.shape,
    pitch: d.pitch,
    size: d.size,
    slantAngle: d.slantAngle,
    pitchMode: d.pitchMode,
  };
}

/** Cascade a project-level stitching defaults change through every part /
 *  line that doesn't carry its own override. Lines with `overridesDefaults`
 *  truthy are left alone; parts with their own `stitchingDefaults` block
 *  shield their lines from a project-level change. */
export function cascadeProjectStitchingDefaults(project: Project): Project {
  return {
    ...project,
    pieces: project.pieces.map((piece) =>
      cascadePartStitchingDefaultsInPlace(project, piece),
    ),
  };
}

/** Cascade a single piece's effective defaults to its non-overriding lines.
 *  Use after editing the project's defaults (every piece reruns) or after
 *  editing a single piece's defaults (call directly for that piece). */
export function cascadePartStitchingDefaultsInPlace(
  project: Project,
  piece: Piece,
): Piece {
  const eff = resolvePartStitchingDefaults(project, piece);
  const lines = pieceStitchLines(piece);
  if (lines.length === 0) return piece;
  return {
    ...piece,
    stitchLines: lines.map((line) =>
      line.overridesDefaults ? line : applyStitchingDefaultsToLine(line, eff),
    ),
  };
}

/** Replace a single piece's stitching-defaults block (or remove it) and
 *  cascade the change to its non-overriding lines. */
export function setPartStitchingDefaults(
  project: Project,
  pieceId: string,
  defaults: StitchingDefaults | undefined,
): Project {
  return {
    ...project,
    pieces: project.pieces.map((piece) => {
      if (piece.id !== pieceId) return piece;
      const next: Piece = { ...piece, stitchingDefaults: defaults };
      return cascadePartStitchingDefaultsInPlace(project, next);
    }),
  };
}

/** Replace the project-level stitching defaults and cascade. */
export function setProjectStitchingDefaults(
  project: Project,
  defaults: StitchingDefaults,
): Project {
  const updated: Project = { ...project, stitchingDefaults: defaults };
  return cascadeProjectStitchingDefaults(updated);
}

/** Per-shape input range for the size field. The unit and meaning of `size`
 *  changes with the chisel: diameter for round, long axis for diamond, and
 *  tooth width (perpendicular to the seam) for slant. */
export interface StitchSizeRange {
  min: number;
  max: number;
  step: number;
  defaultValue: number;
  /** Field label shown in the properties panel. */
  label: string;
}

export function stitchSizeRange(shape: StitchShape): StitchSizeRange {
  if (shape === "round") {
    return {
      min: 0.4,
      max: 5,
      step: 0.1,
      defaultValue: 1,
      label: "Hole diameter",
    };
  }
  // Slant + diamond share the "tooth width" semantic and range. Common
  // pricking irons run roughly 1.2–2.5 mm tooth width; 1.8 mm matches the
  // popular Crimson Hides / Kemovan / KS Blade size most leather workers
  // reach for first.
  return {
    min: 1.2,
    max: 2.5,
    step: 0.05,
    defaultValue: 1.8,
    label: "Tooth width",
  };
}

/** Clamp a stored `size` to the active shape's input range. Used when the
 *  user switches chisel and the previous value is outside the new range — we
 *  snap rather than carrying meaningless leftover state. */
export function clampStitchSize(shape: StitchShape, size: number): number {
  const r = stitchSizeRange(shape);
  return Math.min(r.max, Math.max(r.min, size));
}

/** Default pitch (mm) for a given chisel — applied when the user switches
 *  chisel so the spacing matches what's typical. Round awls and pinholes
 *  are usually punched at 4 mm center-to-center; slant chisels run tighter
 *  at 3.38 mm (the popular SPP equivalent); diamond chisels default to a
 *  3.38 mm gap mode (yielding ~5 mm centers with a 1.6 mm tooth). */
export function defaultPitchForShape(shape: StitchShape): number {
  return shape === "round" ? 4 : 3.38;
}

/** Default pitch-measurement mode for a chisel. Round and slant default
 *  to center-to-center (matches manufacturer specs); diamond defaults to
 *  gap (matches the user's preferred mental model). */
export function defaultPitchModeForShape(shape: StitchShape): StitchPitchMode {
  return shape === "diamond" ? "gap" : "center";
}

/** Suggested tooth width (mm) for a given slant pitch — real pricking irons
 *  scale their tooth width up with their pitch so finer SPI tools have
 *  smaller teeth and coarser ones have wider teeth. Linear ramp from 1.5 mm
 *  at 2 mm pitch to 2.4 mm at 6 mm pitch, rounded to 0.1 mm; pitches outside
 *  that range clamp to the bounds. Applied when the user clicks an mm / SPI
 *  preset on the pitch field. */
export function defaultToothWidthForPitch(pitch: number): number {
  const minPitch = 2.0;
  const maxPitch = 6.0;
  const minTooth = 1.5;
  const maxTooth = 2.4;
  const clamped = Math.max(minPitch, Math.min(maxPitch, pitch));
  const t = (clamped - minPitch) / (maxPitch - minPitch);
  return Math.round((minTooth + t * (maxTooth - minTooth)) * 10) / 10;
}

/** Heuristic for "the user hasn't manually set the tooth width" — true when
 *  the current size matches what the auto-scaler would have produced for
 *  the current pitch. Used to decide whether a pitch preset click should
 *  also adjust the tooth, or leave the user's typed value alone. */
export function isAutoToothForPitch(size: number, pitch: number): boolean {
  return Math.abs(size - defaultToothWidthForPitch(pitch)) < 0.05;
}

/** Slant slot's short-axis (thickness) in mm, scaled to match the pitch.
 *  0.1 mm at 2 mm pitch (and below) up to 0.3 mm at 5 mm pitch (and above).
 *  Real pricking irons have prongs that thicken with the pitch, so the
 *  resulting slits look chunkier on coarser tools and finer on tight ones. */
export function slantSlotThicknessForPitch(pitch: number): number {
  const minPitch = 2;
  const maxPitch = 5;
  const minThickness = 0.1;
  const maxThickness = 0.3;
  const clamped = Math.max(minPitch, Math.min(maxPitch, pitch));
  const t = (clamped - minPitch) / (maxPitch - minPitch);
  return minThickness + t * (maxThickness - minThickness);
}

/** Length the hole takes up along the seam direction. For round and
 *  diamond it's just `size` (their long axis aligns with the seam); for
 *  slant the slot's long axis sits at `slantAngle` from the seam, so the
 *  seam-projection is `slot_length × |cos(slantAngle)|`. Used to convert
 *  a "gap" pitch (inter-prong empty space) into a center-to-center
 *  distance the sampler can use directly. */
export function holeSeamLength(
  shape: StitchShape,
  size: number,
  slantAngle: number,
): number {
  if (shape === "round" || shape === "diamond") return size;
  const sinAbs = Math.abs(Math.sin((slantAngle * Math.PI) / 180));
  if (sinAbs < 0.05) return size;
  const cosAbs = Math.abs(Math.cos((slantAngle * Math.PI) / 180));
  return (size / sinAbs) * cosAbs;
}

/** Resolve the center-to-center pitch a sampler should use, given a stitch
 *  line's stored pitch + mode. "center" returns the stored value as-is;
 *  "gap" adds the hole's seam-projection so the gap reads as the empty
 *  space between adjacent holes. */
export function effectivePitch(line: StitchLine): number {
  const mode: StitchPitchMode = line.pitchMode ?? "center";
  if (mode === "gap") {
    return line.pitch + holeSeamLength(line.shape, line.size, line.slantAngle);
  }
  return line.pitch;
}

/** Same as `effectivePitch` but operating on a raw defaults block — used
 *  during the live drawing preview where there's no committed line yet. */
export function effectivePitchFromDefaults(d: StitchingDefaults): number {
  if ((d.pitchMode ?? "center") === "gap") {
    return d.pitch + holeSeamLength(d.shape, d.size, d.slantAngle);
  }
  return d.pitch;
}

/** Common metric stitch chisel pitches (mm). */
export const STITCH_PITCH_PRESETS_MM = [
  { label: "2.0", value: 2 },
  { label: "2.5", value: 2.5 },
  { label: "3.0", value: 3 },
  { label: "3.38", value: 3.38 },
  { label: "3.85", value: 3.85 },
  { label: "4.0", value: 4 },
  { label: "5.0", value: 5 },
  { label: "6.0", value: 6 },
];

/** Common imperial SPI (stitches per inch) → mm. */
export const STITCH_PITCH_PRESETS_SPI = [
  { label: "5 SPI", value: 25.4 / 5 },
  { label: "6 SPI", value: 25.4 / 6 },
  { label: "7 SPI", value: 25.4 / 7 },
  { label: "8 SPI", value: 25.4 / 8 },
  { label: "9 SPI", value: 25.4 / 9 },
  { label: "10 SPI", value: 25.4 / 10 },
  { label: "11 SPI", value: 25.4 / 11 },
  { label: "12 SPI", value: 25.4 / 12 },
];
